import {
  Grid, Typography, TextField, MenuItem, useMediaQuery,
  useTheme, Box, BoxProps, Card, CardProps, styled, Button,
} from '@mui/material';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import LogoutIcon from '@mui/icons-material/Logout';
import { IAppReduxState, IAccountReduxState } from '../../models';
import { setSelectedMeterID } from '../../reducers/user-input-reducer';
import { SessionService, UtilService } from '../../services';
import { ContainerFrame } from '../frame';
import { CustomerInformation } from './CustomerInformation';
import { CustomerInfoSkeleton } from './CustomerInfoSkeleton';

const Container = styled(Box)<BoxProps>(({ theme }) => ({
  padding: '30px 0 380px 0',
  backgroundColor: theme.palette.primary.dark,
  width: '100%',
  // height: '750px',
}));

const CustomerInformationCard = styled(Card)<CardProps>(({ theme }) => ({
  marginTop: 25,
  width: '100%',
  background: theme.palette.altBackground.dark,
  padding: 25,
  boxShadow: '0px 3px 6px #00000029',
}));

function CustomerDetail({ isLoading }: CustomerDetailProps) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const account = useSelector<IAppReduxState, IAccountReduxState>((state) => state.account);
  const selectedMeterId = useSelector<IAppReduxState, string>((state) => state.userInput.selectedMeterID);

  const customerName = React.useMemo(() => UtilService.modifyCustomerName(account?.customerName), [account?.customerName]);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const isLargeTab = useMediaQuery('(min-width:900px) and (max-width:910px');

  /**
   * Function handle on meter change
   * @returns {void}
   */
  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSelectedMeterID(event.target.value));
  }, [dispatch]);

  const logoutHandler = useCallback((e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();
    SessionService.signOut();
    navigate('/login');
  }, [navigate]);

  const RatePlanHeader = (
    <Grid item xs={12} sm={12} md={8}>
      <Typography variant="h4" color={theme.palette.bluePlan.contrastText}>
        <FormattedMessage id="rate-plan-your-plan-option" />
      </Typography>
      <Typography variant="h5" color={theme.palette.bluePlan.contrastText}>
        <FormattedMessage id="rate-plan-compare-best-plan" />
      </Typography>
    </Grid>
  );

  return (
    <Container theme={theme}>
      <ContainerFrame>
        <Grid container spacing={2} p={2}>
          {!isSmallScreen && !isMediumScreen && !isLargeTab && RatePlanHeader}
          <Grid item xs={7} sm={9} md={isLargeTab ? 10 : 2}>
            <Typography variant="h3" sx={{ typography: { sm: 'h5', xs: 'h5', md: 'h4' } }} color={theme.palette.bluePlan.contrastText}>
              <FormattedMessage id="rate-plan-hello" />
            </Typography>
            <CustomerInfoSkeleton isLoading={isLoading} customerDetail={account}>
              <Typography
                variant="h3"
                sx={{
                  typography: { sm: 'h6', xs: 'h6', md: 'h4' },
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
                color={theme.palette.altBackground.contrastText}
              >
                {customerName}
              </Typography>
            </CustomerInfoSkeleton>
          </Grid>
          <Grid item xs={5} sm={3} md={2} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={logoutHandler}
              endIcon={<LogoutIcon />}
              sx={{
                padding: '0 12px',
                position: 'absolute',
                minWidth: '112px',
                backgroundColor: theme.palette.lightPlan.dark,
                color: theme.palette.mainBackground.contrastText,
              }}
            >
              <FormattedMessage id="congratulations.logout" />
            </Button>
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <CustomerInformationCard theme={theme}>
              <CustomerInformation customerDetail={account} selectedMeterId={selectedMeterId} isLoading={isLoading} />
            </CustomerInformationCard>
          </Grid>
          <Grid item md={isLargeTab ? 1 : 2} container direction="row" justifyContent="center" alignItems="center">
            {!isLargeScreen && (
              <Typography variant="body1" color={theme.palette.bluePlan.contrastText}>
                <FormattedMessage id="rate-plan-electric-number" />
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={12} md={isLargeTab ? 5 : 3} container direction="row" justifyContent="center" alignItems="center">
            <TextField
              label={<FormattedMessage id="rate-plan-meter-number" />}
              select
              value={selectedMeterId}
              onChange={handleChange}
              inputProps={{ 'data-testid': 'meter-dropdown' }}
              variant="filled"
              sx={{ backgroundColor: 'white' }}
              fullWidth
            >
              {account?.servicePoints.map((option) => (
                <MenuItem key={option.meterNumber} value={option.meterNumber}>
                  {option.meterNumber}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {(isMediumScreen || isLargeTab) && (
          <Grid item>
            <Typography variant="h4" mt={3} color={theme.palette.bluePlan.contrastText}>
              <FormattedMessage id="rate-plan-options" />
            </Typography>
          </Grid>
          )}
        </Grid>
      </ContainerFrame>
    </Container>
  );
}

export interface CustomerDetailProps {
  isLoading: boolean;
}

export { CustomerDetail };
